/**
 * LinkedInComposer — focused text editor for LinkedIn posts.
 *
 * Replaces BlockNote in ContentReaderPage center panel when
 * content_type is 'linkedin_post'. Reads/writes social copy for
 * the selected author + linkedin platform.
 *
 * Design: "Editorial Writing Desk" — generous whitespace, LinkedIn
 * blue accents on Martha's warm coral base. Preview card grounds
 * the writing in how it'll appear on LinkedIn.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Hash,
  AtSign,
  Send,
  Calendar,
  CheckCircle,
  ChevronDown,
  ThumbsUp,
  MessageCircle,
  Repeat2,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import {
  type ContentItem,
  type Author,
  type SocialCopy,
} from '../../../api/content';

// ── Constants ────────────────────────────────────────────────────────────────

const LINKEDIN_MAX = 3000;
const LINKEDIN_BLUE = '#0A66C2';
const DEBOUNCE_MS = 1500;

// ── Props ────────────────────────────────────────────────────────────────────

interface LinkedInComposerProps {
  item: ContentItem;
  authors: Author[];
  socialCopies: SocialCopy[];
  workspaceId: string;
  onSaveSocialCopy: (authorId: string, platform: string, text: string) => void;
  onSchedule: (authorIds: string[], scheduledAt?: string) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export function LinkedInComposer({
  item,
  authors,
  socialCopies,
  workspaceId,
  onSaveSocialCopy,
  onSchedule,
}: LinkedInComposerProps) {
  // Filter to authors with LinkedIn profiles
  const linkedInAuthors = useMemo(
    () => authors.filter(a => a.linkedin_profile_url),
    [authors],
  );

  // Default to primary author or first LinkedIn-enabled author
  const [selectedAuthorId, setSelectedAuthorId] = useState<string>(
    () => item.primary_author_id || linkedInAuthors[0]?.id || '',
  );
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [publishing, setPublishing] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Selected author object
  const selectedAuthor = useMemo(
    () => authors.find(a => a.id === selectedAuthorId) ?? linkedInAuthors[0] ?? null,
    [authors, linkedInAuthors, selectedAuthorId],
  );

  // Get the social copy for the selected author + linkedin
  const activeCopy = useMemo(
    () => socialCopies.find(sc => sc.author_id === selectedAuthorId && sc.platform === 'linkedin'),
    [socialCopies, selectedAuthorId],
  );

  const [text, setText] = useState(activeCopy?.copy_text ?? '');

  // Sync text when switching authors
  useEffect(() => {
    const copy = socialCopies.find(sc => sc.author_id === selectedAuthorId && sc.platform === 'linkedin');
    setText(copy?.copy_text ?? '');
  }, [selectedAuthorId, socialCopies]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.max(200, textareaRef.current.scrollHeight) + 'px';
    }
  }, [text]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowAuthorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Character analysis ────────────────────────────────────────────────────

  const charCount = text.length;
  const charPercent = Math.min(100, (charCount / LINKEDIN_MAX) * 100);
  const isOverLimit = charCount > LINKEDIN_MAX;
  const isNearLimit = charPercent > 80;

  const detectedHashtags = useMemo(() => {
    const matches = text.match(/#\w+/g) ?? [];
    return [...new Set(matches)];
  }, [text]);

  const detectedMentions = useMemo(() => {
    const matches = text.match(/@[\w-]+/g) ?? [];
    return [...new Set(matches)];
  }, [text]);

  // ── Auto-save ─────────────────────────────────────────────────────────────

  const handleTextChange = useCallback(
    (value: string) => {
      setText(value);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        if (selectedAuthorId) {
          onSaveSocialCopy(selectedAuthorId, 'linkedin', value);
        }
      }, DEBOUNCE_MS);
    },
    [selectedAuthorId, onSaveSocialCopy],
  );

  // ── Publish / Schedule ────────────────────────────────────────────────────

  const handlePublishNow = useCallback(async () => {
    if (!selectedAuthorId || isOverLimit) return;
    setPublishing(true);
    try {
      onSchedule([selectedAuthorId]);
    } finally {
      setTimeout(() => setPublishing(false), 2000);
    }
  }, [selectedAuthorId, isOverLimit, onSchedule]);

  const handleScheduleSubmit = useCallback(() => {
    if (!selectedAuthorId || !scheduleDate) return;
    onSchedule([selectedAuthorId], new Date(scheduleDate).toISOString());
    setShowSchedulePicker(false);
    setScheduleDate('');
  }, [selectedAuthorId, scheduleDate, onSchedule]);

  // ── Published state ───────────────────────────────────────────────────────

  const isPublished = !!activeCopy?.distributed_at;
  const publishedUrl = activeCopy?.distribution_url;

  // ── Author initial helper ─────────────────────────────────────────────────

  const authorInitial = (name: string) => name.charAt(0).toUpperCase();

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">

      {/* ── Author selector ─────────────────────────────────────────── */}
      {linkedInAuthors.length > 0 && (
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowAuthorDropdown(!showAuthorDropdown)}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-neutral-200
              hover:border-neutral-300 hover:bg-neutral-50/50 transition-all text-left w-full max-w-xs"
          >
            {selectedAuthor && (
              <>
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{ background: LINKEDIN_BLUE }}
                >
                  {authorInitial(selectedAuthor.name)}
                </span>
                <span className="text-sm font-medium text-neutral-800 truncate">
                  {selectedAuthor.name}
                </span>
              </>
            )}
            {!selectedAuthor && (
              <span className="text-sm text-neutral-400">Select author...</span>
            )}
            <ChevronDown size={14} className="text-neutral-400 ml-auto flex-shrink-0" />
          </button>

          {showAuthorDropdown && (
            <div className="absolute top-full left-0 mt-1 w-full max-w-xs bg-white rounded-lg border border-neutral-200 shadow-lg z-20 py-1">
              {linkedInAuthors.map(author => (
                <button
                  key={author.id}
                  onClick={() => {
                    setSelectedAuthorId(author.id);
                    setShowAuthorDropdown(false);
                  }}
                  className={`flex items-center gap-2.5 w-full px-3 py-2 text-left hover:bg-neutral-50 transition-colors
                    ${author.id === selectedAuthorId ? 'bg-blue-50/50' : ''}`}
                >
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                    style={{ background: author.id === selectedAuthorId ? LINKEDIN_BLUE : '#a8a29e' }}
                  >
                    {authorInitial(author.name)}
                  </span>
                  <span className="text-sm text-neutral-700 truncate">{author.name}</span>
                  {author.id === selectedAuthorId && (
                    <CheckCircle size={14} className="ml-auto flex-shrink-0" style={{ color: LINKEDIN_BLUE }} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Composer textarea ───────────────────────────────────────── */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => handleTextChange(e.target.value)}
          placeholder="What do you want to share on LinkedIn?"
          className="w-full resize-none border-0 outline-none focus:ring-0 bg-transparent
            placeholder:text-neutral-300 text-neutral-900"
          style={{
            fontSize: 16,
            lineHeight: 1.75,
            fontFamily: "'Source Serif 4', 'Georgia', serif",
            minHeight: 200,
            letterSpacing: '-0.01em',
          }}
        />

      </div>

      {/* ── Character count bar ─────────────────────────────────────── */}
      <div>
        <div
          className="relative overflow-hidden rounded-full"
          style={{ height: 3, background: 'rgba(231,229,228,0.25)' }}
        >
          <div
            className="absolute left-0 top-0 bottom-0 rounded-full"
            style={{
              width: `${charPercent}%`,
              background: isOverLimit ? '#ef4444' : isNearLimit ? '#d97706' : LINKEDIN_BLUE,
              transition: 'width 200ms ease, background 200ms ease',
            }}
          />
        </div>
        <div className="flex justify-between items-center mt-1.5">
          <div>
            {isOverLimit && (
              <span className="text-[11px] font-medium text-red-500">
                {charCount - LINKEDIN_MAX} over limit
              </span>
            )}
          </div>
          <span
            className="text-[11px] tabular-nums"
            style={{ color: isOverLimit ? '#ef4444' : isNearLimit ? '#d97706' : '#a8a29e' }}
          >
            {charCount.toLocaleString()} / {LINKEDIN_MAX.toLocaleString()}
          </span>
        </div>
      </div>

      {/* ── Hashtag & mention pills ─────────────────────────────────── */}
      {(detectedHashtags.length > 0 || detectedMentions.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {detectedHashtags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md"
              style={{
                color: LINKEDIN_BLUE,
                background: `${LINKEDIN_BLUE}08`,
                border: `1px solid ${LINKEDIN_BLUE}15`,
              }}
            >
              <Hash size={9} />
              {tag.slice(1)}
            </span>
          ))}
          {detectedMentions.map(mention => (
            <span
              key={mention}
              className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md"
              style={{
                color: '#6B9BD1',
                background: 'rgba(107,155,209,0.06)',
                border: '1px solid rgba(107,155,209,0.12)',
              }}
            >
              <AtSign size={9} />
              {mention.slice(1)}
            </span>
          ))}
        </div>
      )}

      {/* ── LinkedIn preview card ───────────────────────────────────── */}
      {text && (
        <div
          className="rounded-xl overflow-hidden"
          style={{
            background: 'rgba(250,250,249,0.8)',
            border: '1px solid rgba(231,229,228,0.5)',
          }}
        >
          {/* Preview header */}
          <div className="px-4 py-3 flex items-center gap-3">
            <span
              className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, rgb(var(--c-coral-500, 217 127 111)) 0%, rgb(var(--c-coral-600, 198 102 86)) 100%)' }}
            >
              {selectedAuthor ? authorInitial(selectedAuthor.name) : 'A'}
            </span>
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-neutral-900 leading-tight truncate">
                {selectedAuthor?.name ?? 'Author'}
              </p>
              <p className="text-[11px] text-neutral-400 leading-tight truncate">
                {selectedAuthor?.role ?? selectedAuthor?.bio?.slice(0, 60) ?? 'LinkedIn preview'}
              </p>
              <p className="text-[10px] text-neutral-300 mt-0.5">Just now</p>
            </div>
          </div>

          {/* Preview body */}
          <div className="px-4 pb-3">
            <p
              className="text-[13px] text-neutral-700 whitespace-pre-wrap break-words"
              style={{ lineHeight: 1.55 }}
            >
              {text.length > 210 ? (
                <>
                  {text.slice(0, 210)}
                  <span className="text-neutral-400">... </span>
                  <span className="font-medium" style={{ color: LINKEDIN_BLUE }}>see more</span>
                </>
              ) : text}
            </p>
          </div>

          {/* Preview reaction bar */}
          <div className="border-t border-neutral-100 px-4 py-2 flex items-center gap-6">
            {[
              { icon: ThumbsUp, label: 'Like' },
              { icon: MessageCircle, label: 'Comment' },
              { icon: Repeat2, label: 'Repost' },
              { icon: Send, label: 'Send' },
            ].map(({ icon: Icon, label }) => (
              <span key={label} className="flex items-center gap-1.5 text-[11px] text-neutral-400 font-medium">
                <Icon size={13} />
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Action bar ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 pt-2 border-t border-neutral-100">

        {isPublished ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
              <CheckCircle size={14} />
              Published
            </span>
            {activeCopy?.distributed_at && (
              <span className="text-xs text-neutral-400">
                {new Date(activeCopy.distributed_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
            )}
            {publishedUrl && (
              <a
                href={publishedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium hover:underline flex items-center gap-1"
                style={{ color: LINKEDIN_BLUE }}
              >
                View post <ExternalLink size={10} />
              </a>
            )}
          </div>
        ) : (
          <>
            <button
              onClick={handlePublishNow}
              disabled={!text || isOverLimit || publishing || !selectedAuthorId}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium
                text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed
                bg-coral-500 hover:bg-coral-600 active:bg-coral-700
                shadow-sm hover:shadow-md"
            >
              {publishing ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Send size={14} />
                  Publish Now
                </>
              )}
            </button>

            <div className="relative">
              <button
                onClick={() => setShowSchedulePicker(!showSchedulePicker)}
                disabled={!text || isOverLimit || !selectedAuthorId}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium
                  transition-all disabled:opacity-40 disabled:cursor-not-allowed
                  text-neutral-700 border border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
              >
                <Calendar size={14} />
                Schedule
              </button>

              {showSchedulePicker && (
                <div className="absolute bottom-full left-0 mb-2 bg-white rounded-lg border border-neutral-200 shadow-lg p-3 z-20 min-w-[260px]">
                  <label className="block text-xs font-medium text-neutral-600 mb-1.5">
                    Schedule for
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduleDate}
                    onChange={e => setScheduleDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full px-2.5 py-1.5 text-sm border border-neutral-200 rounded-lg
                      focus:outline-none focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400"
                  />
                  <div className="flex gap-2 mt-2.5">
                    <button
                      onClick={handleScheduleSubmit}
                      disabled={!scheduleDate}
                      className="flex-1 px-3 py-1.5 text-xs font-medium text-white rounded-lg
                        bg-coral-500 hover:bg-coral-600 disabled:opacity-40 transition-colors"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => { setShowSchedulePicker(false); setScheduleDate(''); }}
                      className="px-3 py-1.5 text-xs font-medium text-neutral-500
                        hover:text-neutral-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

          </>
        )}
      </div>
    </div>
  );
}
